import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Order, Product, Stock, SelectedOption } from '../shared/types';
import { apiService } from '../services/api';
import { getAdminPassword } from '../shared/authConfig';
import { getDisplayAddress } from '../shared/orderUtils';
import './OrderPrint.css';

// ============================================================================
// Types
// ============================================================================

interface AggregatedStockItem {
    stockId: string;
    name: string;
    totalQuantity: number;
    sortOrder: number;
}

interface OrderItemWithLetter {
    letter: string;
    name: string;
    quantity: number;
    price: number;
    productId: string;
    selectedOptions?: SelectedOption[];
}

interface ChoiceDisplay {
    name: string;
    type: 'keep' | 'remove' | 'add' | 'unselected';
}

interface OptionDisplay {
    optionName: string;
    choices: ChoiceDisplay[];
}

// ============================================================================
// Utility Functions
// ============================================================================

function getItemLetter(index: number): string {
    let result = '';
    let n = index;
    do {
        result = String.fromCharCode(65 + (n % 26)) + result;
        n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return result;
}

function aggregateOrderStockItems(order: Order, products: Product[], stocks: Stock[]): AggregatedStockItem[] {
    const stockUsageMap = new Map<string, number>();
    const stockMap = new Map(stocks.map(s => [s.id, s]));
    const productMap = new Map(products.map(p => [p.id, p]));

    for (const item of order.items) {
        const product = productMap.get(item.productId);
        if (!product) continue;

        const itemQuantity = item.quantity || 1;

        if (Array.isArray(product.stockIds)) {
            for (const stockId of product.stockIds) {
                stockUsageMap.set(stockId, (stockUsageMap.get(stockId) ?? 0) + itemQuantity);
            }
        }

        if (Array.isArray(item.selectedOptions) && Array.isArray(product.options)) {
            const optionMap = new Map(product.options.map(opt => [opt.id, opt]));
            for (const selection of item.selectedOptions as SelectedOption[]) {
                const option = optionMap.get(selection.optionId);
                if (!option || !Array.isArray(selection.choiceIds)) continue;

                const choiceMap = new Map(option.choices.map(choice => [choice.id, choice]));
                for (const choiceId of selection.choiceIds) {
                    const choice = choiceMap.get(choiceId);
                    if (!choice || !Array.isArray(choice.stockIds)) continue;

                    for (const stockId of choice.stockIds) {
                        stockUsageMap.set(stockId, (stockUsageMap.get(stockId) ?? 0) + itemQuantity);
                    }
                }
            }
        }
    }

    const aggregatedItems: AggregatedStockItem[] = [];
    for (const [stockId, totalQuantity] of stockUsageMap) {
        if (totalQuantity <= 0) continue;
        const stock = stockMap.get(stockId);
        aggregatedItems.push({
            stockId,
            name: stock?.name || stockId,
            totalQuantity,
            sortOrder: stock?.sortOrder ?? Number.MAX_SAFE_INTEGER,
        });
    }

    aggregatedItems.sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.name.localeCompare(b.name);
    });

    return aggregatedItems;
}

function sortOrderItems(items: Order['items'], products: Product[]): Order['items'] {
    return [...items].sort((a, b) => {
        const productA = products.find(p => p.id === a.productId);
        const productB = products.find(p => p.id === b.productId);
        if (!productA || !productB) return 0;

        const categoryCompare = (productA.category || '').localeCompare(productB.category || '');
        if (categoryCompare !== 0) return categoryCompare;

        return (productA.sortOrder ?? 999999) - (productB.sortOrder ?? 999999);
    });
}

// Map of productId -> optionId -> Set of visible choiceIds
type VisibleChoicesMap = Map<string, Map<string, Set<string>>>;

/**
 * Build a map of which choices should be visible for each product.
 * A choice is visible if:
 * - It is default-selected on the product definition, OR
 * - It is customer-selected on at least one order item of that product
 */
function buildVisibleChoicesMap(items: OrderItemWithLetter[], products: Product[]): VisibleChoicesMap {
    const map: VisibleChoicesMap = new Map();

    // Group items by productId
    const itemsByProduct = new Map<string, OrderItemWithLetter[]>();
    for (const item of items) {
        const existing = itemsByProduct.get(item.productId) || [];
        existing.push(item);
        itemsByProduct.set(item.productId, existing);
    }

    // For each unique product in the order
    for (const [productId, productItems] of itemsByProduct) {
        const product = products.find(p => p.id === productId);
        if (!product?.options) continue;

        const optionMap = new Map<string, Set<string>>();

        for (const option of product.options) {
            const visibleChoiceIds = new Set<string>();

            for (const choice of option.choices) {
                // Show if default-selected
                if (choice.defaultSelected) {
                    visibleChoiceIds.add(choice.id);
                    continue;
                }

                // Show if customer-selected on ANY item of this product
                for (const item of productItems) {
                    const selectedOption = item.selectedOptions?.find(so => so.optionId === option.id);
                    if (selectedOption?.choiceIds?.includes(choice.id)) {
                        visibleChoiceIds.add(choice.id);
                        break;
                    }
                }
            }

            optionMap.set(option.id, visibleChoiceIds);
        }

        map.set(productId, optionMap);
    }

    return map;
}

function getOptionDisplays(
    item: OrderItemWithLetter,
    products: Product[],
    visibleChoicesMap: VisibleChoicesMap
): OptionDisplay[] {
    const product = products.find(p => p.id === item.productId);
    if (!product?.options) return [];

    const productVisibility = visibleChoicesMap.get(item.productId);
    const optionDisplays: OptionDisplay[] = [];

    // Sort options by order
    const sortedOptions = [...product.options].sort((a, b) => {
        const aOrder = a.order ?? 999999;
        const bOrder = b.order ?? 999999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.name.localeCompare(b.name);
    });

    for (const option of sortedOptions) {
        const selectedOption = item.selectedOptions?.find(so => so.optionId === option.id);
        const selectedChoiceIds = selectedOption?.choiceIds || [];
        const visibleChoiceIds = productVisibility?.get(option.id) || new Set<string>();

        // Sort choices by order
        const sortedChoices = [...option.choices].sort((a, b) => {
            const aOrder = a.order ?? 999999;
            const bOrder = b.order ?? 999999;
            if (aOrder !== bOrder) return aOrder - bOrder;
            return a.name.localeCompare(b.name);
        });

        const choices: ChoiceDisplay[] = [];
        for (const choice of sortedChoices) {
            // Skip choices that are not in the master list for this product group
            // Master list excludes ONLY: not default AND not selected on ANY item
            if (!visibleChoiceIds.has(choice.id)) {
                continue;
            }

            const isSelected = selectedChoiceIds.includes(choice.id);
            const isDefault = choice.defaultSelected || false;

            if (isDefault && isSelected) {
                // Default selected and kept - plain text
                choices.push({ name: choice.name, type: 'keep' });
            } else if (isDefault && !isSelected) {
                // Default selected but removed - strikethrough in box
                choices.push({ name: choice.name, type: 'remove' });
            } else if (!isDefault && isSelected) {
                // Not default but added - name in box (bold)
                choices.push({ name: choice.name, type: 'add' });
            } else {
                // Not default, not selected on THIS item, but in master list
                // (another item of same product has it) - strikethrough without box
                choices.push({ name: choice.name, type: 'unselected' });
            }
        }

        if (choices.length > 0) {
            optionDisplays.push({ optionName: option.name, choices });
        }
    }

    return optionDisplays;
}

// ============================================================================
// Column Types
// ============================================================================

type PrintColumn = 
    | { type: 'stock'; stockItems: AggregatedStockItem[]; customerName: string; address: string }
    | { type: 'product'; item: OrderItemWithLetter; optionDisplays: OptionDisplay[]; customerName: string; address: string };

// ============================================================================
// Main Component
// ============================================================================

export default function OrderPrint() {
    const { orderId } = useParams<{ orderId: string }>();
    const navigate = useNavigate();
    const [order, setOrder] = useState<Order | null>(null);
    const [products, setProducts] = useState<Product[]>([]);
    const [stocks, setStocks] = useState<Stock[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Auth check
    useEffect(() => {
        const adminPass = getAdminPassword();
        if (!adminPass) {
            navigate(`/melaba/login?redirect=/melaba/order-print/${orderId}`);
        }
    }, [navigate, orderId]);

    const fetchData = useCallback(async () => {
        if (!orderId) {
            setError('No order ID provided');
            setLoading(false);
            return;
        }

        try {
            const [orderData, productsData, stocksData] = await Promise.all([
                apiService.getOrderById(orderId),
                apiService.getProducts(),
                apiService.getStocks()
            ]);
            setOrder(orderData);
            setProducts(productsData);
            setStocks(stocksData);
            setLoading(false);
        } catch (err) {
            console.error('Failed to fetch order data:', err);
            setError('Failed to load order. Please check the order ID.');
            setLoading(false);
        }
    }, [orderId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Trigger print after data loads
    useEffect(() => {
        if (!loading && !error && order) {
            const timer = setTimeout(() => {
                window.print();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [loading, error, order]);

    // Auth check
    const adminPass = getAdminPassword();
    if (!adminPass) {
        return null;
    }

    if (loading) {
        return (
            <div className="order-print-container">
                <div className="print-loading">Loading order...</div>
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="order-print-container">
                <div className="print-error">
                    <h2>Error</h2>
                    <p>{error || 'Order not found'}</p>
                    <button onClick={() => navigate('/melaba/orders')}>Back to Orders</button>
                </div>
            </div>
        );
    }

    // Prepare columns
    const sortedItems = sortOrderItems(order.items, products);
    const itemsWithLetters: OrderItemWithLetter[] = sortedItems.map((item, index) => ({
        ...item,
        letter: getItemLetter(index)
    }));

    // Build visible choices map for smart choice hiding
    const visibleChoicesMap = buildVisibleChoicesMap(itemsWithLetters, products);

    const stockItems = aggregateOrderStockItems(order, products, stocks);
    const customerName = order.customerName || 'No name';
    const address = getDisplayAddress(order, 'No address');

    // Build columns: 1 stock column + N product columns
    // Expand items with quantity > 1 into multiple identical columns
    const productColumns: PrintColumn[] = [];
    for (const item of itemsWithLetters) {
        const quantity = item.quantity || 1;
        const optionDisplays = getOptionDisplays(item, products, visibleChoicesMap);
        
        // Create 'quantity' number of identical columns for this item
        for (let i = 0; i < quantity; i++) {
            productColumns.push({
                type: 'product' as const,
                item: { ...item, quantity: 1 }, // Each column represents 1 item
                optionDisplays,
                customerName,
                address
            });
        }
    }

    const allColumns: PrintColumn[] = [
        { type: 'stock', stockItems, customerName, address },
        ...productColumns
    ];

    // Split into pages of 5 columns each
    const pages: PrintColumn[][] = [];
    for (let i = 0; i < allColumns.length; i += 5) {
        pages.push(allColumns.slice(i, i + 5));
    }

    return (
        <div className="order-print-container">
            {pages.map((pageColumns, pageIndex) => (
                <div key={pageIndex} className="print-page">
                    <div className="print-columns" style={{ '--column-count': pageColumns.length } as React.CSSProperties}>
                        {pageColumns.map((column, colIndex) => (
                            <div key={colIndex} className="print-column">
                                {column.type === 'stock' ? (
                                    <StockColumn
                                        stockItems={column.stockItems}
                                        customerName={column.customerName}
                                        address={column.address}
                                    />
                                ) : (
                                    <ProductColumn
                                        item={column.item}
                                        optionDisplays={column.optionDisplays}
                                        customerName={column.customerName}
                                        address={column.address}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ============================================================================
// Stock Column Component
// ============================================================================

interface StockColumnProps {
    stockItems: AggregatedStockItem[];
    customerName: string;
    address: string;
}

function StockColumn({ stockItems, customerName, address }: StockColumnProps) {
    return (
        <div className="column-content">
            <div className="customer-info">
                <div className="customer-name">{customerName}</div>
                <div className="customer-address">{address}</div>
            </div>
            <div className="stock-title">STOCK</div>
            <div className="stock-list">
                {stockItems.map(item => (
                    <div key={item.stockId} className="stock-row">
                        <span className="stock-qty">{item.totalQuantity}×</span>
                        <span className="stock-name">{item.name}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ============================================================================
// Product Column Component
// ============================================================================

interface ProductColumnProps {
    item: OrderItemWithLetter;
    optionDisplays: OptionDisplay[];
    customerName: string;
    address: string;
}

function ProductColumn({ item, optionDisplays, customerName, address }: ProductColumnProps) {
    return (
        <div className="column-content">
            <div className="customer-info">
                <div className="customer-name">{customerName}</div>
                <div className="customer-address">{address}</div>
            </div>
            <div className="item-letter">{item.letter}</div>
            <div className="product-name">{item.name}</div>
            <div className="product-options">
                {optionDisplays.map((optDisplay, optIdx) => (
                    <div key={optIdx} className="option-group">
                        <div className="option-title">{optDisplay.optionName}</div>
                        <div className="option-choices">
                            {optDisplay.choices.map((choice, choiceIdx) => (
                                <div key={choiceIdx} className="choice-row">
                                    <span className={`choice-text choice-${choice.type}`}>
                                        {choice.name}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
